/**
 * From the Facebook messages view from within Facebook (not the dedicated
 * Messenger site), delete individual and group chats, and leave community
 * chats.
 *
 * Note: this *will* remove chats from user's accounts across devices, but it
 * *will not* remove those messages from others in those chats; to do that,
 * all messages would need to be unsent.
 *
 * The algorithm work as follows:
 * - the Messenger UX has a display with many *rows*
 *    - most rows correspond to a individual chat, group chat, or community chat
 *      and have a "more options" menu that allows you to leave or delete
 *    - there is one special row, the "Marketplace" row, which instead of opening
 *      a single chat, takes you to a new view with new rows, corresponding to
 *      all the Marketplace chats (the marketplace chats are just like individual
 *      chat rows)
 * - for the general rows, we open the "more options" menu and check the options
 *     - individual and group chats have both "leave" and "delete"
 *     - community chats only have "leave"
 * - once you select "leave" or "delete", a dialog modal will pop up asking you
 *   to confirm
 *     - it will have be titled with the option, have a confirm button with text
 *       "leave" or "delete", as well as a cancel option
 * - if affirmed, the row corresponding to the chat will disappear from the UX
 *
 * We start by iterating through all rows in the main UX; if we find a
 * Marketplace row, we track it and save it for later. Once we get to the end,
 * we return to the Marketplace row, click it to see the Marketplace rows, then
 * iterate through the Marketplace rows.
 */
import { v4 as uuidv4 } from 'uuid'

import { NO_TIMEOUT } from '$extensionSrc/consts/stageMachineConsts'
import { FacebookSettingKey } from '$extensionSrc/recommendations/recommendationSettingsKeys'
import {
  FBRecommendationKey,
  PLATFORM,
  StageType,
} from '$extensionSrc/utils/enums'
import { _debug } from '$extensionSrc/utils/logging'
import { remoteLog } from '$extensionSrc/utils/remoteLog'

import { StageDef } from '../automationMachineInterfaces'
import { setStoredMachineOverlayStatus } from '../automationStageMachine'
import { throwIfGlobalInterruptRequested } from '../globalInterrupt'
import {
  defaultCantUndoFn,
  defaultIsDisabledFn,
  defaultIsDisabledReasonFn,
} from '../recommendationCardDisplayUtils'
import { getElement, getOptionalElement } from '../utils'
import {
  evaluateXpath,
  getCaseInsensitiveAttribute,
  getCaseInsensitiveString,
} from '../xpath'
import { FACEBOOK_AUTOMATION_STAGE_STATUSES } from './facebookAutomationStageStatuses'
import * as fbUrls from './fbUrls'
import { getDomPromise, throwIfTimeout, wait } from '../interruptiblePromises'

const getCurrentValueFn = () => () => ''
const getIsFulfilledFn = () => () => true
const getCantUndoFn = () => defaultCantUndoFn
const getIsDisabledFn = () => defaultIsDisabledFn
const getIsDisabledReasonFn = () => defaultIsDisabledReasonFn

/* Types */

export enum RowType {
  MARKETPLACE = 'MARKETPLACE',
  GENERAL = 'GENERAL',
  UNKNOWN = 'UNKNOWN',
}

type Row = {
  node: HTMLElement
  type: RowType
}

export enum ChatMenuOption {
  DELETE = 'DELETE',
  LEAVE = 'LEAVE',
}

type ChatMenu = {
  node: HTMLElement
  options: ChatMenuOption[]
}

type ConfirmationDialog = {
  node: HTMLElement
  option: ChatMenuOption
}

/**
 * Row iteration algorithm
 *
 * In order to make processing each row simpler, we write an async iterator.
 * The logic works as follows:
 * 1. Using xpath & the promises, wait for the rows to appear (see `getRows`)
 *    - the "rows" have "role = row" and have a descendant that has either
 *        - a "more options" aria label
 *        - a "marketplace" text span
 *    - we ignore any "seen" rows (see step 2)
 * 2. Update each row element for our internal purposes
 *    - add a "seen" attribute so we don't get stuck looping over the same row
 *    - add an "id" attribute if not already there
 * 3. Yield the rows one by one
 * 4. Loop through 1-3 until `getRows` no longer returns any rows
 */

// Randomly generate an attrname to avoid overwriting an actual attribute
function generateSeenAttrname() {
  const uid = uuidv4().replace('-', '').slice(0, 6)
  return `seen${uid}`
}

/**
 * Get all row elements visible on the screen, using a promise to wait for them
 * to appear. If the promise times out, we would return an empty list.
 */
export async function getRows(
  contextNode: Node,
  seenAttrname: string,
): Promise<HTMLElement[]> {
  const pattern = `
    //*[@role="row" and not(@${seenAttrname} = "true") and count(./descendant::*[
      starts-with(${getCaseInsensitiveAttribute('aria-label')}, "more options") or
      ${getCaseInsensitiveString('text()')} = "marketplace"
    ]) > 0]
  `
  // Give rows opportunity to appear
  await getOptionalElement(contextNode, pattern, { timeout: 6000 })

  // Return all visible rows
  return evaluateXpath<HTMLElement>(contextNode, pattern)
}

/**
 * Differentiate between the special "Marketplace" row that navigates to the
 * marketplace chat ux and all other chat rows
 */
export function getRowType(row: HTMLElement): RowType {
  // Check for special Marketplace row:
  // - no more options menu
  // - yes marketplace span
  const isMarketplacePattern = `
    ./self::*[
      count(./descendant::*[
        starts-with(${getCaseInsensitiveAttribute('aria-label')}, "more options")
      ]) = 0 and 
      count(./descendant::*[
        ${getCaseInsensitiveString('text()')} = "marketplace"
      ]) > 0
    ]
  `

  const hasMoreOptionsMenuPattern = `
    ./descendant::*[
      starts-with(${getCaseInsensitiveAttribute('aria-label')}, "more options")
    ]
  `

  const isMarketplace = evaluateXpath(row, isMarketplacePattern).length > 0
  const hasMoreOptionsMenu =
    evaluateXpath(row, hasMoreOptionsMenuPattern).length > 0

  if (isMarketplace) {
    return RowType.MARKETPLACE
  } else if (hasMoreOptionsMenu) {
    return RowType.GENERAL
  }
  return RowType.UNKNOWN
}

/**
 * Continually look for rows under the given context node until we no longer
 * find any new rows.
 */
export async function* iterateRows(
  contextNode: Node,
  seenAttrname: string,
): AsyncGenerator<Row, void, void> {
  let rows: HTMLElement[] = []
  while (true) {
    // Handle global interrupts
    throwIfGlobalInterruptRequested()

    // If no rows to yield, try to find more
    if (rows.length === 0) {
      rows = await getRows(contextNode, seenAttrname)

      // Update rows to label if we've seen them, and to tag them with an id
      rows.forEach((x) => {
        x.setAttribute(seenAttrname, 'true')

        if (!x.id) {
          x.setAttribute('id', uuidv4())
        }
      })

      // If no rows were found, end the iterator
      if (rows.length === 0) {
        return
      }
    }

    const cur = rows.shift()

    if (cur) {
      yield {
        node: cur,
        type: getRowType(cur),
      }
    }
  }
}

/**
 * Utilities for the deletion menus
 *
 * We first open the "more options" menu for each row, then handle a
 * confirmation dialog.
 *
 * The two options we look for are "leave" and "delete"; each also has its own
 * confirmation dialog.
 */

/**
 * Get text used in menu & modals for each option
 */
export function getTextForOption(option: ChatMenuOption): string {
  switch (option) {
    case ChatMenuOption.DELETE:
      return 'delete '
    case ChatMenuOption.LEAVE:
      return 'leave '
    default:
      throw new Error(`Unrecognized option ${option}`)
  }
}

/**
 * Inspect menu element to figure out which options are present in it
 */
export function getChatMenuOptions(menu: HTMLElement): ChatMenuOption[] {
  const found: ChatMenuOption[] = []
  for (const option of [ChatMenuOption.DELETE, ChatMenuOption.LEAVE]) {
    const pattern = `./descendant::*[contains(
      ${getCaseInsensitiveString('text()')},
      "${getTextForOption(option)}"
    )]`

    if (evaluateXpath(menu, pattern).length > 0) {
      found.push(option)
    }
  }

  return found
}

/**
 * This method can be called once the more options menu has been opened (by
 * clicking on the right child of the row element).
 *
 * It searches the document for the more options menu by looking for any of our
 * recognized menu options.
 *
 * Returns null if no menu found
 */
export async function getMoreOptionsMenu(): Promise<ChatMenu | null> {
  const containFragment = [ChatMenuOption.DELETE, ChatMenuOption.LEAVE]
    .map(
      (option) =>
        `contains(${getCaseInsensitiveString('text()')}, "${getTextForOption(option)}")`,
    )
    .join(' or ')

  const pattern = `//*[
    @role = "menu" and
    count(./descendant::*[${containFragment}]) > 0
  ]`

  const node = await getOptionalElement<HTMLElement>(document, pattern, {
    timeout: 500,
  })

  if (!node) {
    return null
  }

  return {
    node,
    options: getChatMenuOptions(node),
  }
}

/**
 * Given a row element, get the button that opens the "more options" menu.
 */
export async function getRowMenuButton(row: HTMLElement): Promise<HTMLElement> {
  const buttonPattern = `./descendant::*[
    starts-with(${getCaseInsensitiveAttribute('aria-label')}, "more options")
  ]`

  return getElement<HTMLElement>(row, buttonPattern)
}

/**
 * Given a row element, get and click the row menu button, and wait for the
 * more options menu.
 */
async function openRowMenu(row: HTMLElement): Promise<ChatMenu | null> {
  const button = await getRowMenuButton(row)
  button.click()

  return getMoreOptionsMenu()
}

/**
 * Find the confirmation dialog element for the given option type
 */
export async function getConfirmationDialog(
  menuOption: ChatMenuOption,
): Promise<HTMLElement> {
  const pattern = `//*[
    @role = "dialog" and
    count(./descendant::*[contains(
      ${getCaseInsensitiveString('text()')},
      "${getTextForOption(menuOption)}"
    )]) > 0
  ]`
  return getElement<HTMLElement>(document, pattern)
}

/**
 * Given a menu, click the provided option and get the resultant confirmation
 * dialog.
 */
async function openConfirmationDialog(
  menu: HTMLElement,
  menuOption: ChatMenuOption,
): Promise<ConfirmationDialog> {
  const buttonPattern = `./descendant::*[
    contains(${getCaseInsensitiveString('text()')}, "${getTextForOption(menuOption)}")
  ]`

  const button = await getElement<HTMLElement>(menu, buttonPattern)
  button.click()

  const node = await getConfirmationDialog(menuOption)

  return {
    node,
    option: menuOption,
  }
}

/**
 * Wait for the provided row to be removed from the dom tree (with a timeout)
 */
async function getRowRemovedPromise(row: HTMLElement): Promise<any> {
  // Use the row's id to make it easier to check for
  let id = row.id
  if (!id) {
    id = uuidv4()
    row.setAttribute('id', id)
  }

  _debug(`row ${row.id}: awaiting removal`, 'fbDeleteChats', 'd')

  // Make sure the row is still there
  if (!document.getElementById(id)) {
    _debug(`row ${row.id}: not found; continuing`, 'fbDeleteChats', 'd')
    return Promise.resolve()
  }

  // Set up promise
  return Promise.race([
    getDomPromise(document, () => !document.getElementById(id)),
    throwIfTimeout('Timeout waiting for row to disappear', 5000),
  ])
}

export async function getConfirmationDialogButton(
  dialogNode: Node,
  ariaLabel: string,
): Promise<HTMLElement> {
  const pattern = `./descendant::*[
    @role = "button" and
    not(@aria-hidden = "true") and
    not(@aria-disabled = "true") and
    contains(${getCaseInsensitiveAttribute('aria-label')}, "${ariaLabel.toLowerCase()}")
  ]`

  return getElement<HTMLElement>(dialogNode, pattern)
}

/**
 * Handle the confirmation dialog:
 * - find confirm button
 * - click
 * - wait for row to disappear
 *
 * In dry mode, we click "cancel" and use the "remove" method to artificially
 * remove the row.
 */
async function handleConfirmationDialog(
  dialog: ConfirmationDialog,
  row: HTMLElement,
  isDry: boolean,
) {
  const rowRemoved = getRowRemovedPromise(row)

  if (isDry) {
    const cancelButton = await getConfirmationDialogButton(
      dialog.node,
      'cancel',
    )
    cancelButton.click()
    row.remove()
  } else {
    const confirmButton = await getConfirmationDialogButton(
      dialog.node,
      getTextForOption(dialog.option),
    )
    confirmButton.click()
  }

  await rowRemoved
}

/**
 * End-to-end handling for a general chat row
 * - open menu
 * - if there is a delete option, first try to delete
 * - else, if there is a leave option, try to leave
 * - else, skip the row since there are no recognized options
 *
 * Return a boolean var for whether or not we successfully removed the row
 */
export async function processRow(row: Row, isDry: boolean): Promise<boolean> {
  _debug(`row: ${row.node.id} - opening menu`, 'fbDeleteChats', 'd')

  // Check for valid menu; if none exit early
  const menu = await openRowMenu(row.node)

  if (!menu) {
    return false
  }

  let success = true
  if (menu.options.includes(ChatMenuOption.LEAVE)) {
    _debug(`row: ${row.node.id} - handling with leaving`, 'fbDeleteChats', 'd')
    const dialog = await openConfirmationDialog(menu.node, ChatMenuOption.LEAVE)
    await handleConfirmationDialog(dialog, row.node, isDry)
  } else if (menu.options.includes(ChatMenuOption.DELETE)) {
    _debug(`row: ${row.node.id} - handling with deletion`, 'fbDeleteChats', 'd')
    const dialog = await openConfirmationDialog(
      menu.node,
      ChatMenuOption.DELETE,
    )
    await handleConfirmationDialog(dialog, row.node, isDry)
  } else {
    _debug(
      `row: ${row.node.id} - no recognized options; skipping`,
      'fbDeleteChats',
      'd',
    )
    success = false
  }

  return success
}

/**
 * Marketplace utilities
 *
 * Handle navigation into and back out of the view with Marketplace chats
 */

async function openMarketplaceTab(marketplaceRow: Row): Promise<HTMLElement> {
  const label = await getElement<HTMLElement>(
    marketplaceRow.node,
    `//*[${getCaseInsensitiveString('text()')} = "marketplace"]`,
  )
  label.click()
  return getElement<HTMLElement>(
    document,
    `//*[@role="grid" and ${getCaseInsensitiveAttribute('aria-label')} = "marketplace"]`,
  )
}

async function closeMarketplaceTab(): Promise<void> {
  const backButton = await getOptionalElement<HTMLElement>(
    document,
    `//*[${getCaseInsensitiveAttribute('aria-label')}="back" and @role="button"]`,
  )
  if (backButton) {
    backButton.click()
  }
}

/**
 * Stage Defs
 */

const gatherStageDefs: StageDef[] = []

const updateStageDefs: StageDef[] = [
  {
    type: StageType.ACTION,
    key: 'FACEBOOK_DELETING_CHATS',
    status: FACEBOOK_AUTOMATION_STAGE_STATUSES.DELETING_CHATS,
    url: fbUrls.messengerUrl,
    // eslint-disable-next-line security/detect-non-literal-regexp -- fbUrls.messengerUrl is a trusted constant
    urlPattern: new RegExp(`${fbUrls.messengerUrl}.+`),
    timeout: NO_TIMEOUT,
    action: async (machineContext, userContext, additionalMachineData) => {
      const {
        isDry,
        incrementTotalCount,
        updateStatisticsLastCount,
        updateStoredInitialValues,
        recommendationProgress,
      } = userContext

      const { resumeData } = additionalMachineData

      // Generate a new attrname to use to track which rows have been seen
      // We generate a new one each run so that when we unpause we don't
      // skip any rows that were visible but unprocessed before pausing.
      const seenAttrname = generateSeenAttrname()

      // Set up statistics tracking
      const prevTotalCt =
        recommendationProgress?.[FBRecommendationKey.FB_DELETE_CHATS]
          ?.statistics?.totalCount || 0
      let ct = resumeData.data?.ct || 0
      let foundUnrecognizedRow = resumeData.data?.foundUnrecognizedRow || false
      let foundAtLeastOneChat = resumeData.data?.foundAtLeastOneChat || false
      const incrementStats = async () => {
        ct += 1
        await resumeData.set({
          ct,
          foundUnrecognizedRow,
          foundAtLeastOneChat,
        })
        await incrementTotalCount(FBRecommendationKey.FB_DELETE_CHATS)
        await updateStatisticsLastCount(FBRecommendationKey.FB_DELETE_CHATS, ct)
      }
      const updateOverlayAndBanner = async () => {
        /**
         * Cases we try to handle:
         * - pluralization
         * - no chats found
         * - there were chats we found that we didn't know how to process
         */
        const totalCt = prevTotalCt + ct

        await setStoredMachineOverlayStatus(PLATFORM.FACEBOOK, {
          ...FACEBOOK_AUTOMATION_STAGE_STATUSES.DELETING_CHATS,
          nDeleted: ct,
        })

        let message
        let title
        let detail
        if (totalCt === 0) {
          message = `No chats deleted.`
        } else if (totalCt === 1) {
          message = `Deleted 1 chat`
        } else {
          message = `Deleted ${totalCt} total chats`
        }

        if (ct === 0) {
          if (foundAtLeastOneChat) {
            message = 'No chats deleted.'
            title = 'No chats deleted'
          } else {
            message = 'No chats found.'
            detail = 'We were unable to find any chats.'
            title = 'No chats found'
          }
        } else if (ct === 1) {
          title = 'Deleted 1 chat'
        } else {
          title = `Deleted ${ct} chats`
        }

        if (foundUnrecognizedRow) {
          detail = `Note: we were unable to delete some chats`
        } else {
          detail = `Still see more chats? Try running the feature again!
          Sometimes Facebook is slow to load or process requests, leading to
          incomplete results.`
        }

        await updateStoredInitialValues(
          {
            [FacebookSettingKey.DELETE_CHATS]: {
              message,
              result: {
                detail,
                title,
              },
            },
          },
          FBRecommendationKey.FB_DELETE_CHATS,
        )
      }

      // Iterate through all rows; if we find marketplace row, save for later
      let marketplaceRow: Row | undefined
      for await (const row of iterateRows(document, seenAttrname)) {
        _debug(`row: ${row.node.id}`, 'fbDeleteChats', 'd')

        foundAtLeastOneChat = true

        if (row.type === RowType.UNKNOWN) {
          remoteLog.info('FB_DELETE_CHATS: Row of unknown type')
          foundUnrecognizedRow = true

          // eslint-disable-next-line no-continue
          continue
        }

        if (row.type === RowType.MARKETPLACE) {
          _debug(
            `row: ${row.node.id} - skip; type is MARKETPLACE`,
            'fbDeleteChats',
            'd',
          )
          marketplaceRow = row

          // eslint-disable-next-line no-continue
          continue
        }

        let success
        let processRowError
        try {
          success = await processRow(row, isDry)
        } catch (e) {
          processRowError = e
          success = false
        }

        if (success) {
          // Short wait; seems to help deletion go smoothly
          await wait(250)
          await incrementStats()
        } else {
          remoteLog.info(
            `FB_DELETE_CHATS: processRow failed to remove row (error: ${processRowError})`,
          )
          foundUnrecognizedRow = true
        }

        await updateOverlayAndBanner()
      }
      await updateOverlayAndBanner()

      // If there was a marketplace row, handle all marketplace rows
      if (marketplaceRow) {
        const marketplaceTab = await openMarketplaceTab(marketplaceRow)
        for await (const row of iterateRows(marketplaceTab, seenAttrname)) {
          _debug(`row: ${row.node.id}`, 'fbDeleteChats', 'd')

          if (row.type !== RowType.GENERAL) {
            remoteLog.info(
              'FB_DELETE_CHATS: Found unexpected row in Marketplace section',
            )
            foundUnrecognizedRow = true

            // eslint-disable-next-line no-continue
            continue
          }

          let success
          let processRowError
          try {
            success = await processRow(row, isDry)
          } catch (e) {
            processRowError = e
            success = false
          }

          if (success) {
            // Short wait; seems to help deletion go smoothly
            await wait(250)
            await incrementStats()
          } else {
            remoteLog.info(
              `FB_DELETE_CHATS: processRow failed to remove row (error: ${processRowError})`,
            )
            foundUnrecognizedRow = true
          }
          await updateOverlayAndBanner()
        }

        await closeMarketplaceTab()
      }

      await updateOverlayAndBanner()
    },
  },
]

const undoStageDefs: StageDef[] = []

export {
  getCantUndoFn,
  getCurrentValueFn,
  getIsDisabledFn,
  getIsDisabledReasonFn,
  getIsFulfilledFn,
  gatherStageDefs,
  updateStageDefs,
  undoStageDefs,
}
